import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import MobileHeader from "../../components/MobileHeader";
import BottomNav from "../../components/BottomNav";
import LanguageSwitcher from "../../components/LanguageSwitcher";
import InstallAppPrompt from "../../components/InstallAppPrompt";
import { Card, Button, Input, Label, StatusBadge } from "../../components/ui-kit";
import { playNotificationSound, unlockNotificationSound } from "../../lib/notificationSound";
import { SignOut, FileArrowUp, Camera, Bell, SlidersHorizontal, ShieldCheck } from "@phosphor-icons/react";

export default function DriverProfile() {
  const { user, profile, logout, refresh } = useAuth();
  const workerRole = user?.role === "courier" ? "courier" : "driver";
  const workerBase = workerRole === "courier" ? "/courier" : "/driver";
  const v = profile?.vehicle;
  const dp = profile?.driver_profile;
  const [details, setDetails] = useState({
    legal_full_name: "",
    id_number: "",
    home_address: "",
    operating_address: "",
    emergency_contact_name: "",
    emergency_contact_phone: "+243",
    vehicle_type: "moto",
    vehicle_plate: "",
    vehicle_model: "",
  });
  const [saving, setSaving] = useState(false);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(() => localStorage.getItem("nexora-notification-sound") !== "off");
  const [preferences, setPreferences] = useState({
    auto_accept: false,
    max_pickup_distance_km: 12,
    service_modes: ["ride", "scheduled"],
    preferred_vehicle_types: [],
    accepts_for_someone_else: true,
    safety_pin_required: true,
    women_rider_preference: false,
    area_filter_enabled: false,
  });
  const [savingPreferences, setSavingPreferences] = useState(false);

  useEffect(() => {
    setDetails({
      legal_full_name: dp?.legal_full_name || user?.name || "",
      id_number: dp?.id_number || "",
      home_address: dp?.home_address || "",
      operating_address: dp?.operating_address || "",
      emergency_contact_name: dp?.emergency_contact_name || "",
      emergency_contact_phone: dp?.emergency_contact_phone || "+243",
      vehicle_type: v?.vehicle_type || "moto",
      vehicle_plate: v?.plate || "",
      vehicle_model: v?.model || "",
    });
  }, [dp, user, v]);

  useEffect(() => {
    api.get("/driver/preferences")
      .then(({ data }) => setPreferences(data))
      .catch(() => {});
  }, []);

  const onChange = (key) => (e) => setDetails((current) => ({ ...current, [key]: e.target.value }));

  const saveDetails = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/driver/details", details);
      await refresh();
      toast.success("Details chauffeur mis a jour");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhotoUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/profile/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await refresh();
      toast.success("Photo de profil mise a jour");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setPhotoUploading(false);
      e.target.value = "";
    }
  };

  const toggleSound = async () => {
    const next = !soundEnabled;
    localStorage.setItem("nexora-notification-sound", next ? "on" : "off");
    setSoundEnabled(next);
    if (next) {
      await unlockNotificationSound();
      playNotificationSound();
    }
  };

  const togglePref = (key) => setPreferences((current) => ({ ...current, [key]: !current[key] }));
  const toggleMode = (mode) => setPreferences((current) => {
    const set = new Set(current.service_modes || []);
    if (set.has(mode)) set.delete(mode);
    else set.add(mode);
    return { ...current, service_modes: Array.from(set) };
  });
  const togglePreferredType = (type) => setPreferences((current) => {
    const set = new Set(current.preferred_vehicle_types || []);
    if (set.has(type)) set.delete(type);
    else set.add(type);
    return { ...current, preferred_vehicle_types: Array.from(set) };
  });
  const savePreferences = async () => {
    setSavingPreferences(true);
    try {
      const { data } = await api.put("/driver/preferences", preferences);
      setPreferences(data);
      toast.success("Preferences chauffeur sauvegardees");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingPreferences(false);
    }
  };

  return (
    <div className="phone-frame phone-scroll-safe bg-premium-grad">
      <MobileHeader title="Profil Chauffeur" />
      <div className="space-y-4 px-4 pt-2">
        <Card className="p-4">
          <div className="flex flex-wrap items-center gap-3">
            <label className="relative flex h-14 w-14 cursor-pointer items-center justify-center overflow-hidden rounded-2xl bg-gradient-to-br from-primary to-secondary text-xl font-bold text-primary-foreground">
              {user?.profile_photo_url ? (
                <img src={user.profile_photo_url} alt={user?.name || "Profil"} className="h-full w-full object-cover" />
              ) : (
                user?.name?.[0]
              )}
              <span className="absolute bottom-1 right-1 rounded-full bg-black/60 p-1"><Camera size={12} weight="bold" /></span>
              <input type="file" accept="image/*" className="hidden" onChange={uploadPhoto} disabled={photoUploading} />
            </label>
            <div className="min-w-0 flex-1">
              <div className="truncate font-heading text-xl font-bold">{user?.name}</div>
              <div className="font-mono text-xs text-muted-foreground">{user?.phone}</div>
              <div className="truncate font-mono text-xs text-muted-foreground">{user?.email}</div>
              <div className="mt-1 flex gap-2">
                <StatusBadge status={dp?.approval_status || "PENDING_VALIDATION"} />
                <StatusBadge status={dp?.status || "offline"} />
              </div>
            </div>
          </div>
        </Card>

        <InstallAppPrompt compact />

        {v && (
          <Card className="p-4">
            <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Véhicule</div>
            <div className="font-heading text-xl font-bold">{v.vehicle_type.toUpperCase()}</div>
            <div className="font-mono text-xs">{v.plate} · {v.model}</div>
            <div className="mt-2"><StatusBadge status={v.status} /></div>
          </Card>
        )}

        <Card className="p-4">
          <div className="text-[10px] font-bold uppercase tracking-widest text-primary">Details obligatoires</div>
          <form onSubmit={saveDetails} className="mt-3 space-y-3">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <Label>Identite complete</Label>
                <Input value={details.legal_full_name} onChange={onChange("legal_full_name")} required />
              </div>
              <div>
                <Label>Numero piece</Label>
                <Input value={details.id_number} onChange={onChange("id_number")} required />
              </div>
            </div>
            <div>
              <Label>Adresse complete</Label>
              <Input value={details.home_address} onChange={onChange("home_address")} required />
            </div>
            <div>
              <Label>Zone ou adresse de travail</Label>
              <Input value={details.operating_address} onChange={onChange("operating_address")} required />
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <Label>Contact urgence</Label>
                <Input value={details.emergency_contact_name} onChange={onChange("emergency_contact_name")} required />
              </div>
              <div>
                <Label>Telephone urgence</Label>
                <Input value={details.emergency_contact_phone} onChange={onChange("emergency_contact_phone")} required />
              </div>
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <div>
                <Label>Type</Label>
                <select
                  value={details.vehicle_type}
                  onChange={onChange("vehicle_type")}
                  className="w-full rounded-xl border border-white/10 bg-white/5 px-3 py-3 text-foreground focus:border-primary focus:outline-none"
                >
                  {["moto", "economy", "comfort", "premium", "xl"].map((item) => (
                    <option key={item} value={item} className="bg-background">{item}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label>Plaque</Label>
                <Input value={details.vehicle_plate} onChange={onChange("vehicle_plate")} required />
              </div>
              <div>
                <Label>Modele</Label>
                <Input value={details.vehicle_model} onChange={onChange("vehicle_model")} required />
              </div>
            </div>
            <Button type="submit" variant="primary" className="w-full" disabled={saving}>
              {saving ? "Enregistrement..." : "Enregistrer les details"}
            </Button>
          </form>
        </Card>

        <div className="grid grid-cols-2 gap-3">
          <Card className="p-3 text-center">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Courses</div>
            <div className="font-heading text-2xl font-bold">{dp?.total_rides || 0}</div>
          </Card>
          <Card className="p-3 text-center">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Note</div>
            <div className="font-heading text-2xl font-bold">★ {dp?.rating?.toFixed(1) || "5.0"}</div>
          </Card>
        </div>

        <Card className="space-y-3 p-4">
          <div>
            <div className="font-heading font-bold">Preferences chauffeur</div>
            <div className="text-[11px] text-muted-foreground">Alertes de demandes, securite et disponibilite</div>
          </div>
          <button
            type="button"
            onClick={toggleSound}
            className="flex w-full items-center justify-between gap-3 rounded-2xl bg-white/5 p-3 text-left"
            data-testid="driver-notification-sound"
          >
            <span className="flex min-w-0 items-center gap-3">
              <span className="rounded-xl bg-secondary/15 p-2 text-secondary"><Bell size={18} weight="duotone" /></span>
              <span className="min-w-0">
                <span className="block text-sm font-bold">Son des demandes</span>
                <span className="block text-[11px] text-muted-foreground">Alertes sonores type messagerie</span>
              </span>
            </span>
            <span className={`shrink-0 rounded-full px-3 py-1.5 text-[10px] font-bold uppercase ${soundEnabled ? "bg-secondary text-secondary-foreground" : "bg-white/10 text-muted-foreground"}`}>
              {soundEnabled ? "Actif" : "Coupe"}
            </span>
          </button>
          <div className="rounded-2xl bg-white/5 p-3">
            <div className="flex items-start gap-3">
              <span className="rounded-xl bg-primary/15 p-2 text-primary"><SlidersHorizontal size={18} weight="duotone" /></span>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-bold">Matching avance</div>
                <div className="mt-1 text-[11px] text-muted-foreground">
                  Ces choix filtrent les demandes visibles et le score chauffeur.
                </div>
              </div>
            </div>
            <div className="mt-3 grid gap-2">
              {[
                ["auto_accept", "Auto-accept trip requests"],
                ["accepts_for_someone_else", "Accepter les courses pour une autre personne"],
                ["safety_pin_required", "Toujours demander le PIN securite"],
                ["women_rider_preference", "Preference demandes femmes/familles"],
                ["area_filter_enabled", "Filtre de zone active"],
              ].map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => togglePref(key)}
                  className="flex w-full items-center justify-between rounded-xl bg-black/15 p-3 text-left"
                  data-testid={`driver-pref-${key}`}
                >
                  <span className="text-xs font-bold">{label}</span>
                  <span className={`h-5 w-9 rounded-full p-0.5 transition ${preferences[key] ? "bg-secondary" : "bg-white/15"}`}>
                    <span className={`block h-4 w-4 rounded-full bg-white transition ${preferences[key] ? "translate-x-4" : ""}`} />
                  </span>
                </button>
              ))}
            </div>
            <div className="mt-3">
              <Label>Distance max pickup: {preferences.max_pickup_distance_km} km</Label>
              <input
                type="range"
                min="1"
                max="80"
                value={preferences.max_pickup_distance_km}
                onChange={(e) => setPreferences((current) => ({ ...current, max_pickup_distance_km: Number(e.target.value) }))}
                className="w-full"
                data-testid="driver-pref-distance"
              />
            </div>
            <div className="mt-3">
              <Label>Modes de service</Label>
              <div className="flex flex-wrap gap-2">
                {[
                  ["ride", "Taxi"],
                  ["scheduled", "Programmee"],
                  ["shuttle", "Shuttle"],
                ].map(([mode, label]) => (
                  <button
                    key={mode}
                    type="button"
                    onClick={() => toggleMode(mode)}
                    className={`rounded-full border px-3 py-2 text-[10px] font-bold uppercase tracking-widest ${preferences.service_modes?.includes(mode) ? "border-primary/40 bg-primary/15 text-primary" : "border-white/10 bg-white/5 text-muted-foreground"}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
            <div className="mt-3">
              <Label>Types preferes</Label>
              <div className="flex flex-wrap gap-2">
                {["moto", "economy", "comfort", "premium", "xl"].map((type) => (
                  <button
                    key={type}
                    type="button"
                    onClick={() => togglePreferredType(type)}
                    className={`rounded-full border px-3 py-2 text-[10px] font-bold uppercase tracking-widest ${preferences.preferred_vehicle_types?.includes(type) ? "border-secondary/40 bg-secondary/15 text-secondary" : "border-white/10 bg-white/5 text-muted-foreground"}`}
                  >
                    {type}
                  </button>
                ))}
              </div>
              <div className="mt-2 flex items-start gap-2 text-[11px] text-muted-foreground">
                <ShieldCheck size={14} weight="fill" className="mt-0.5 shrink-0 text-secondary" />
                Laissez vide pour accepter tous les types compatibles avec votre vehicule.
              </div>
            </div>
            <Button onClick={savePreferences} variant="secondary" className="mt-3 w-full text-xs" disabled={savingPreferences}>
              {savingPreferences ? "Sauvegarde..." : "Sauvegarder les preferences"}
            </Button>
          </div>
        </Card>

        <Link to={`${workerBase}/documents`} data-testid="docs-link">
          <Card className="flex items-center justify-between p-4 hover:bg-white/10">
            <div className="flex items-center gap-3">
              <div className="rounded-xl bg-primary/15 p-2 text-primary"><FileArrowUp size={20} weight="bold" /></div>
              <div>
                <div className="font-heading font-bold">Mes documents</div>
                <div className="text-[11px] text-muted-foreground">Permis, carte grise, CIN, assurance</div>
              </div>
            </div>
            <div className="text-primary">→</div>
          </Card>
        </Link>

        <Link to={`${workerBase}/support`} data-testid="driver-support-link">
          <Card className="flex items-center justify-between p-4 hover:bg-white/10">
            <div className="flex items-center gap-3">
              <div className="rounded-xl bg-secondary/15 p-2 text-secondary"><FileArrowUp size={20} weight="bold" /></div>
              <div>
                <div className="font-heading font-bold">Support chauffeur</div>
                <div className="text-[11px] text-muted-foreground">Litiges, paiements, incidents et remboursements</div>
              </div>
            </div>
            <div className="text-primary">→</div>
          </Card>
        </Link>

        <Button variant="outline" className="w-full" onClick={logout} data-testid="driver-logout">
          <SignOut weight="bold" /> Se déconnecter
        </Button>
        <div className="flex items-center justify-center pt-2">
          <LanguageSwitcher />
        </div>
      </div>
      <BottomNav role={workerRole} />
    </div>
  );
}
